import React, { useState, useContext, useCallback, useEffect } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { NetworkStatus } from 'apollo-client';

import type { UIAnalyticsEventHandler, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { AnalyticsListener } from '@atlaskit/analytics-next';
import { Box, xcss, Flex } from '@atlaskit/primitives';

import { fg } from '@confluence/feature-gating';
import type { BannerStateContainer } from '@confluence/banners';
import { ContentTypesHeader } from '@confluence/content-types-header';
import { FIXED_OBJECT_HEADER_SSR_HEIGHT } from '@confluence/content-types-header/entry-points/contentTypesHeaderConstants';
import type { ContentTypeShellProps } from '@confluence/content-types-bridge';
import { ContentTypesHeaderPlaceholder } from '@confluence/content-types-header-base';
import { DATABASE_EXPERIENCE, ExperienceTrackerContext } from '@confluence/experience-tracker';
import { ContentViewedEvent } from '@confluence/content-body';
import { useIsMobileApp } from '@confluence/mobile-app-detection';
import { shouldShowMobileWeb } from '@confluence/mobile-detection';
import { DATABASE_VIEW } from '@confluence/named-routes';
import { NotFoundContained } from '@confluence/content-prerequisites';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils';
import { useCommentsDataActions } from '@confluence/comments-data';
import { useSessionData } from '@confluence/session-data';
import { PageSegmentLoadStart } from '@confluence/browser-metrics';
import { ArchivedPageBanner } from '@confluence/archive-pages';
import {
	useIsVersionHistoryEnabled,
	useResizeBanner,
	useVersionHistoryExperienceTracking,
} from '@confluence/content-types-utils';
import {
	useIsSidePanelOpen,
	useSidePanelStateActions,
} from '@confluence/side-panel-state-container';
import { VersionHistory } from '@confluence/version-history';
import { ContentTypeEventService } from '@confluence/content-types-bridge/entry-points/content-types-event-service';
import { useRegisterCommentsPanelCallbacks } from '@confluence/content-types-bridge/entry-points/useRegisterCommentsPanelCallbacks';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState';
import { useSkippableCoordination } from '@confluence/skippable-coordination-client';
import { VersionHistoryModalManager } from '@confluence/databases-onboarding';
import {
	ObjectSidebarControl,
	ObjectSidebarPanel,
	ObjectSidebarPushPlaceholder,
} from '@confluence/object-sidebar-components';
import {
	useIsObjectSidebarShown,
	useObjectSidebarActions,
	useBehavior,
} from '@confluence/object-sidebar-api';
import {
	OnboardingNthUserContentTypeSpotlight,
	ContentType,
} from '@confluence/experiment-onboarding-content-type-spotlight/entry-points/OnboardingNthUserContentTypeSpotlight';
import { ContentTypesObjectSidebarService } from '@confluence/content-types-bridge';

import { DatabaseOnboarding } from './DatabaseOnboarding';
import { MobileWebView } from './components/DatabaseMobileView';
import { DATABASE_VIEW_PAGE_CONTENT_SEGMENT, StartDatabasePageLoad } from './perf.config';
import { databaseHeightContainer } from './database-shell-styles';
import type { DatabaseIdentity } from './database-shell-types';
import { useDatabaseHeaderActions, useOnboardingService } from './hooks';
import { useDatabaseParams } from './database-params';
import { DatabaseIframe } from './components/DatabaseIframe';
import { useDatabaseVersionHistory } from './hooks/useDatabaseVersionHistory';

const databaseShellContainerStyles = xcss({
	height: '100%',
	width: '100%',
	position: 'relative',
	overflow: 'hidden',
});

const contentHeaderWrapperStyles = xcss({
	borderBottomWidth: 'border.width',
	borderBottomStyle: 'solid',
	borderBottomColor: 'color.border',
	backgroundColor: 'elevation.surface',
});

const archivedBannerContainerStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
	width: '100%',
});

// If any additional fixed margins (like the fixed archive banner margin we used to have here) are
// needed for SSR, then add them to SSR_HEADER_HEIGHT because in SSR we can't use ResizeObserver to
// tell us how big the headers actually are.
const SSR_HEADER_HEIGHT = FIXED_OBJECT_HEADER_SSR_HEIGHT;

type DatabaseShellProps = ContentTypeShellProps & {
	spaceId?: string | null;
	spaceName?: string | null;
	identity?: DatabaseIdentity;
	dataSecurityPolicyPageExport: boolean | null;
};

export const DatabaseShell = ({
	spaceKey,
	contentId,
	contentReferenceId,
	loading,
	queryNetworkStatus,
	spaceId,
	spaceName,
	title,
	identity,
	refetch,
	dataSecurityPolicyPageExport,
}: DatabaseShellProps) => {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR);
	const isLoadingNewContent = loading && queryNetworkStatus !== NetworkStatus.refetch;

	const { bannerHeight: archivedBannerHeight, setBannerElement: setArchivedBannerHeight } =
		useResizeBanner(isSSR);

	const {
		bannerHeight: databaseHeaderHeight,
		setBannerElement: setDatabaseHeaderElement,
		setBannerHeight: setDatabaseHeaderHeight,
	} = useResizeBanner(isSSR);

	const { isMobileApp } = useIsMobileApp();
	const shouldShowMobileWebView = shouldShowMobileWeb() && !isMobileApp;
	const contentActions = useDatabaseHeaderActions();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const onboardingService = useOnboardingService();
	const { userId } = useSessionData();
	const [eventService] = useState(() => new ContentTypeEventService());
	const [objectSidebarService] = useState(() => new ContentTypesObjectSidebarService());

	const params = useDatabaseParams();
	const isSmartLinkEmbed = params['isSmartLinkEmbed'];

	const dbVersionHistory = useDatabaseVersionHistory();
	const isVersionHistoryEnabled = useIsVersionHistoryEnabled('database');
	const isSidePanelOpen = useIsSidePanelOpen();
	const { hideSidePanel } = useSidePanelStateActions();

	const isObjectSidebarShown = useIsObjectSidebarShown();
	const behavior = useBehavior();
	const { hideObjectSidebar } = useObjectSidebarActions();

	// Used to clear existing comments data when we leave the space overview page
	const { resetInitialDataLoadedMap } = useCommentsPanelActions();
	const { clearCommentsData } = useCommentsDataActions();
	useRegisterCommentsPanelCallbacks();

	const [hasStartedVersionHistoryOnboardingCoordination, stopVersionHistoryOnboardingCoordination] =
		useSkippableCoordination(
			'cc-database-version-history-onboarding-2',
			!fg('confluence_frontend_db_version_history_onboarding'),
		);

	const onCloseVersionHistoryModal = useCallback(() => {
		void stopVersionHistoryOnboardingCoordination();
	}, [stopVersionHistoryOnboardingCoordination]);

	const [showVersionHistorySpotlight, setShowVersionHistorySpotlight] = useState(false);

	const onConfirmVersionHistoryModal = useCallback(() => {
		setShowVersionHistorySpotlight(true);
	}, [setShowVersionHistorySpotlight]);

	const showVersionHistoryOnboardingModal =
		hasStartedVersionHistoryOnboardingCoordination &&
		fg('confluence_frontend_db_version_history_onboarding');

	const routeDataRef = useRouteDataRef();

	useEffect(() => {
		return () => {
			if (fg('cc-whiteboards-consolidating-comments')) {
				resetInitialDataLoadedMap();
				clearCommentsData();
			}
		};
	}, [clearCommentsData, resetInitialDataLoadedMap, contentId]);

	useEffect(() => {
		return () => {
			hideSidePanel();
		};
	}, [hideSidePanel]);

	useEffect(() => {
		// hide sidebar when navigating between different databases
		hideObjectSidebar();
	}, [contentId, hideObjectSidebar]);

	useEffect(() => {
		// hide the object sidebar panels when we open version history.
		// when we have the version history panel open, the sidebar control is not visible,
		// so we will never run into the case where we have version history open and then the user
		// opens the comments/details panel
		if (isObjectSidebarShown && isSidePanelOpen) {
			hideObjectSidebar();
		}
	}, [isObjectSidebarShown, isSidePanelOpen, hideObjectSidebar]);

	const updateDatabaseHeaderEvents: UIAnalyticsEventHandler = useCallback(
		(event: UIAnalyticsEvent) => {
			const newData = {
				objectType: 'database',
			};

			const newAttributes = {
				contentType: 'database',
			};

			event.update((payload) => {
				const mergedAttributes = {
					...payload.data?.attributes,
					...newAttributes,
				};
				return {
					...payload,
					data: {
						...payload.data,
						...newData,
						attributes: mergedAttributes,
					},
				};
			});
		},
		[],
	);

	// Always show the Object Header if enabled by the feature gate - but keep the current behavior
	// for the old content header which isn't shown in SSR
	const isHeaderShown = !isSSR || fg('confluence_frontend_object_header');
	const showContentTypesHeader =
		isHeaderShown && !isLoadingNewContent && !params.isSmartLinkEmbed && !!userId;

	// Use a fixed height for the SSR'ed Object Header to avoid layout shift, because ResizeObserver is not available in SSR
	const contentHeaderHeight =
		isSSR && fg('confluence_frontend_object_header') ? SSR_HEADER_HEIGHT : databaseHeaderHeight;
	const headerHeight = archivedBannerHeight + contentHeaderHeight;

	const showContentTypesHeaderPlaceholder =
		isLoadingNewContent && !params.isSmartLinkEmbed && !!userId;

	const openSidePanel = isSidePanelOpen && fg('confluence_frontend_databases_version_history_ui');

	const { trackExperience } = useVersionHistoryExperienceTracking();

	useEffect(() => {
		const flowQueryParam = routeDataRef.current?.queryParams?.flow;

		if (flowQueryParam === 'restoreVersion') {
			trackExperience({
				contentType: 'database',
				experience: 'restoreExperience',
				task: 'succeed',
			});
		}
	}, [routeDataRef, contentId, trackExperience]);

	if (!loading && !contentReferenceId) {
		experienceTracker.fail({
			name: DATABASE_EXPERIENCE,
			error: new Error('DatabaseUnifiedQuery did not return valid referenceId'),
		});
		return <NotFoundContained route={DATABASE_VIEW} spaceKey={spaceKey} />;
	}

	return (
		<>
			<DatabaseOnboarding onboardingService={onboardingService} />
			<StartDatabasePageLoad contentId={contentId} />
			<Subscribe to={databaseHeightContainer}>
				{(bannerState: BannerStateContainer) => (
					<Flex direction="column" xcss={databaseShellContainerStyles}>
						{showContentTypesHeaderPlaceholder && <ContentTypesHeaderPlaceholder />}
						{showContentTypesHeader && (
							<AnalyticsListener onEvent={updateDatabaseHeaderEvents}>
								{isVersionHistoryEnabled ? (
									<Box
										testId="pinned-ct-header"
										xcss={contentHeaderWrapperStyles}
										ref={setDatabaseHeaderElement}
									>
										<ContentTypesHeader
											contentId={contentId}
											contentActions={contentActions}
											showVersionHistorySpotlight={showVersionHistorySpotlight}
											setShowVersionHistorySpotlight={setShowVersionHistorySpotlight}
										/>
									</Box>
								) : (
									<ContentTypesHeader
										contentId={contentId}
										contentActions={contentActions}
										onHeightChange={setDatabaseHeaderHeight}
									/>
								)}
							</AnalyticsListener>
						)}
						{!isSmartLinkEmbed && (
							<Box xcss={archivedBannerContainerStyles} ref={setArchivedBannerHeight}>
								<Box>
									<ArchivedPageBanner contentId={contentId} marginStyle="both" />
								</Box>
							</Box>
						)}
						{shouldShowMobileWebView ? (
							<MobileWebView bannerState={bannerState} />
						) : (
							<>
								<PageSegmentLoadStart metric={DATABASE_VIEW_PAGE_CONTENT_SEGMENT} />
								<DatabaseIframe
									contentId={contentId}
									contentReferenceId={contentReferenceId}
									spaceKey={spaceKey}
									dataSecurityPolicyPageExport={dataSecurityPolicyPageExport}
									identity={identity}
									refetch={refetch}
									spaceId={spaceId}
									spaceName={spaceName}
									title={title}
									loading={loading}
									isSmartLinkEmbed={isSmartLinkEmbed}
									headerHeight={headerHeight}
									bannerState={bannerState}
									isSidePanelOpen={isSidePanelOpen}
									dbVersionHistory={dbVersionHistory}
									objectSidebarService={objectSidebarService}
								/>
								<OnboardingNthUserContentTypeSpotlight
									contentType={ContentType.DATABASE}
									contentId={contentId}
								/>
								{!openSidePanel && fg('confluence_frontend_databases_content_wrapper') && (
									<ObjectSidebarControl
										contentId={contentId}
										hasCommentsButton={fg('cc-whiteboards-consolidating-comments')}
										hasAiButton
										hasDetailsButton
										spaceKey={spaceKey}
										contentType="database"
										hasLoomButton
									/>
								)}
								{openSidePanel && (
									<VersionHistory
										headerHeight={bannerState.getTotalHeight(true) + databaseHeaderHeight}
										contentId={contentId}
										title={title}
										panelTop={databaseHeaderHeight}
										eventService={eventService}
										setVersion={dbVersionHistory.setDatabaseVersion}
										contentType="database"
									/>
								)}
								{isObjectSidebarShown && (
									<>
										{behavior === 'push' && <ObjectSidebarPushPlaceholder />}
										<ObjectSidebarPanel ignoreGap />
									</>
								)}
								{!isSSR && <ContentViewedEvent contentId={contentId} />}
							</>
						)}
					</Flex>
				)}
			</Subscribe>

			{showVersionHistoryOnboardingModal && (
				<VersionHistoryModalManager
					onClose={onCloseVersionHistoryModal}
					onConfirm={onConfirmVersionHistoryModal}
				/>
			)}
		</>
	);
};
